import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useWorkspace } from '@/providers/workspace-provider'
import { useAuth } from '@/providers/auth-provider'
import type { CommentWithAuthor } from '@/types/domain'

export function useComments(taskId: string | null) {
  return useQuery({
    queryKey: ['comments', taskId ?? 'none'],
    enabled: !!taskId,
    queryFn: async (): Promise<CommentWithAuthor[]> => {
      const { data, error } = await supabase
        .from('comments')
        .select('*, author:profiles!comments_author_id_fkey(id,full_name,avatar_url,email)')
        .eq('task_id', taskId!)
        .is('deleted_at', null)
        .order('created_at', { ascending: true })
        .returns<CommentWithAuthor[]>()
      if (error) throw error
      return data ?? []
    },
  })
}

export function useAddComment(taskId: string) {
  const { user } = useAuth()
  const { workspaceId } = useWorkspace()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ body, mentions }: { body: string; mentions: string[] }) => {
      const { error } = await supabase
        .from('comments')
        .insert({ task_id: taskId, author_id: user?.id ?? null, body, mentions })
      if (error) throw error

      // Notifica cada usuário mencionado.
      if (mentions.length && workspaceId) {
        await supabase.from('notifications').insert(
          mentions
            .filter((id) => id !== user?.id)
            .map((uid) => ({
              workspace_id: workspaceId,
              user_id: uid,
              type: 'mencao' as const,
              title: 'Você foi mencionado',
              body,
              entity_type: 'task',
              entity_id: taskId,
            })),
        )
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comments', taskId] }),
  })
}
